<template>
  <label
    class="input-date"
    v-bind:class="tags">
    <span
      class="input-date__label"
      v-if="!labelHidden">{{ label }}</span>
    <input
      class="input-date__input"
      data-lpignore="true"
      :pattern="pattern"
      :placeholder="mask"
      ref="input"
      type="text"
      inputmode="numeric"
      v-bind:aria-invalid="hasViolations"
      v-bind:disabled="disabled"
      v-bind:required="!optional"
      v-bind:title="title"
      v-model="maskedValue"
      v-on:input="onInput"
      v-on:keydown="onKeydown"
      v-on:blur="onBlur" />
    <p
      class="input-date__violation"
      role="alert"
      v-for="text in violations"
      v-bind:key="text"
    >{{ text }}</p>
  </label>
</template>
<script>

/**
 * @description Ввод даты.
 * @type {Vue}
 */
export default {
  computed: {
    /**
     * @description Имеет нарушения.
     * @type {boolean}
     */
    hasViolations() {
      return this.violations.length > 0;
    },
    tags() {
      const list = [];
      if (this.violations.length > 0) {
        list.push('invalid');
      }
      return list;
    },
    /**
     * @description Заголовок по наведению.
     * @type {(string|null)}
     */
    title() {
      if (this.labelHidden !== true) {
        return null;
      }
      return this.label;
    },
  },
  data() {
    return {
      maskedValue: this.applyMask(this.value),
    };
  },
  watch: {
    value(newValue) {
      this.maskedValue = this.applyMask(newValue);
    },
  },
  methods: {
    /**
     * @description Форматируем значение по маске "дд/мм/гггг"
     * @returns {string}
     */
    applyMask(value) {
      const newValue = value.replace(/\D/g, '');
      if (newValue === '') {
        return newValue;
      }
      const { mask } = this;
      let result = '';
      let valueIndex = 0;
      for (let i = 0; i < mask.length; i += 1) {
        if (/[дмг]/.test(mask[i])) {
          if (valueIndex < newValue.length) {
            result += newValue[valueIndex];
            valueIndex += 1;
          } else {
            result += mask[i];
          }
        } else {
          result += mask[i];
        }
      }
      return result;
    },
    /**
     * @description Обрабатываем ввод данных
     * @returns {void}
     */
    onInput(event) {
      const rawValue = event.target.value.replace(/\D/g, '');
      const newValue = this.getFormattedValue(rawValue);
      this.$emit('input', newValue);
      this.$nextTick(() => {
        this.maskedValue = this.applyMask(newValue);
      });
    },
    /**
     * @description Удаляем строго с конца и корректно возвращаем маску.
     * @returns {void}
     */
    onKeydown(event) {
      if (event.key === 'Backspace') {
        event.preventDefault();
        const value = this.maskedValue.replace(/\D/g, '');
        if (value.length === 0) return;
        const newValue = this.getFormattedValue(value.slice(0, -1));
        this.$emit('input', newValue);
        this.$nextTick(() => {
          this.maskedValue = this.applyMask(newValue);
        });
      }
    },
    /**
     * @description Приводим данные к формату "дд/мм/гггг"
     * @returns {string}
     */
    getFormattedValue(value) {
      const parts = value.match(/(\d{0,2})(\d{0,2})(\d{0,4})/);
      return `${parts[1] ? parts[1] : 'дд'}/${parts[2] ? parts[2] : 'мм'}/${parts[3] ? parts[3] : 'гггг'}`;
    },
    /**
     * @description Обработать потерю фокуса.
     * @param {InputEvent} event Событие.
     * @param event
     */
    onBlur(event) {
      this.$emit('blur', event.target.value);
    },
  },
  name: 'InputDate',
  props: {
    /**
     * @description Отключен.
     */
    disabled: {
      default: () => false,
      type: Boolean,
    },
    /**
     * @description Заголовок.
     * @type {string}
     */
    label: {
      required: true,
      type: String,
    },
    /**
     * @description Заголовок должен быть скрыт.
     * @type {boolean}
     */
    labelHidden: {
      default: () => false,
      required: false,
      type: Boolean,
    },
    /**
     * @description Опционально.
     */
    optional: {
      default: () => true,
      type: Boolean,
    },
    /**
     * @description Значение.
     * @type {string}
     */
    value: {
      default: () => '',
      required: false,
      type: String,
    },
    /**
     * @description Список нарушений.
     * @type {string[]}
     */
    violations: {
      default: () => [],
      required: false,
      type: Array,
    },
    /**
     * @description Маска.
     * @type {string}
     *
     */
    mask: {
      type: String,
      required: false,
      default: () => 'дд/мм/гггг',
    },
    /**
     * @description Паттерн проверки
     * @type {string}
     */
    pattern: {
      type: String,
      required: false,
      default: () => '^[0-3]\\d\\/[01]\\d\\/\\d{4}$',
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";
@use "@/scss/fields";
.input-date {
  display: flex;
  flex-direction: column;
  &__label {
    color: var(--unfocus-color);
    font-size: .75rem;
    font-weight: 400;
    line-height: 1rem;
    margin: 0 0 2px 0;
  }
  &__input {
    @include fields.input-field();
  }
  &.invalid &__input {
    color: var(--warning-color);
  }
  &__violation {
    color: var(--warning-color);
    font-size: 12px;
    line-height: 14px;
    font-weight: 400;
    padding: .125rem;
    background-color: transparent !important;
  }
}
</style>
